import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary';
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage';
import Showcases from '@components/sentinel/Showcases/Showcases';
import ShowcaseSkeleton from '@components/sentinel/skeleton/ShowcaseSkeleton/ShowcaseSkeleton';
import MarkdownMessage from '@gluedigital/markdown-message';
import usePageId from '@hooks/usePageId';
import phaseIcons from '@static/vectors/sentinel/home/phases';
import sentinelLogo from '@static/vectors/sentinel/home/sentinel-logo.svg';
import { VIDEO_URL_PATTERN } from '@utils/videoParser';
import { FormEvent, Suspense, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { PHASES, TIKTOK_URL_REGEX, TIKTOK_VIDEO_URL_REGEX } from 'src/constants/sentinel';
import { resetProcess } from 'src/store/slices/sentinel';
import { useAppDispatch, useAppSelector } from 'universal-scripts';
import './SentinelHome.css';

export default function SentinelHome() {
	usePageId('sentinel');
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const status = useAppSelector((state) => state.sentinel.status);

	useEffect(() => {
		if (status !== 'idle') {
			dispatch(resetProcess());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [urlInput, setUrlInput] = useState('');
	const [deepFakeDetection, setDeepFakeDetection] = useState(false);
	const [refreshCache, setRefreshCache] = useState(false);
	const [urlError, setUrlError] = useState(false);

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const url = urlInput.trim();
		if (!url) return;
		// TikTok videos must use the canonical /@user/video/<id> form expected by the pipeline.
		if (TIKTOK_URL_REGEX.test(url) && !TIKTOK_VIDEO_URL_REGEX.test(url)) {
			setUrlError(true);
			return;
		}
		setUrlError(false);
		navigate(
			`/sentinel/process?url=${encodeURIComponent(url)}${deepFakeDetection ? '&deepFakeDetection=true' : ''}${refreshCache ? '&refresh=true' : ''}`
		);
	};

	return (
		<section id="sentinel-home">
			<div className="container">
				<img src={sentinelLogo} alt="trueflag icon" />
				<h1>
					<MarkdownMessage id="sentinel-home.title" />
				</h1>
				<p>
					<FormattedMessage id="sentinel-home.description" />
				</p>
				<section id="sentinel-phases">
					{PHASES.map((phase) => (
						<article id={`phase-card-${phase}`} className="phase-card" key={phase}>
							<section className="bot-wrapper">
								<img src={phaseIcons[phase]} alt={`${phase} icon`} />
							</section>
							<section>
								<h2>
									<FormattedMessage id={`sentinel-home.phase.${phase}.title`} />
								</h2>
								<p>
									<FormattedMessage id={`sentinel-home.phase.${phase}.description`} />
								</p>
							</section>
						</article>
					))}
				</section>
				<form onSubmit={handleSubmit} className="sentinel-form">
					<fieldset>
						<input
							type="url"
							value={urlInput}
							onChange={(e) => {
								setUrlInput(e.target.value);
								if (urlError) setUrlError(false);
							}}
							placeholder="URL (TikTok / YouTube Shorts)"
							pattern={VIDEO_URL_PATTERN}
							title="TikTok/YT Shorts URL"
							required
						/>
					</fieldset>
					<fieldset>
						<label className="deep-fake-detection">
							<input onChange={(e) => setDeepFakeDetection(e.target.checked)} checked={deepFakeDetection} type="checkbox" />
							<span>
								{' '}
								<FormattedMessage id="sentinel-home.deep-fake-detection" />
							</span>
						</label>
						<label className="refresh-cache">
							<input onChange={(e) => setRefreshCache(e.target.checked)} checked={refreshCache} type="checkbox" />
							<span>
								{' '}
								<FormattedMessage id="sentinel-home.refresh-cache" />
							</span>
						</label>
						<label className={!urlInput.trim() ? '' : 'disabled'}>
							<input disabled={!!urlInput.trim()} type="file" name="video" accept="video/*" id="upload-video" />
							<span className="icon icon-plus" />
							<span>
								{' '}
								<FormattedMessage id="sentinel-home.upload-video" />
							</span>
						</label>
						<button type="submit" disabled={!urlInput.trim()}>
							<span className="icon icon-play" />
							<span>
								<FormattedMessage id="sentinel-home.run-button" />
							</span>
						</button>
					</fieldset>
				</form>
				{urlError && (
					<p className="url-error" role="alert">
						<FormattedMessage id="sentinel-home.invalid-tiktok-url" />
					</p>
				)}
				<ErrorBoundary errorComponent={ErrorMessage}>
					<Suspense fallback={<ShowcaseSkeleton />}>
						<Showcases />
					</Suspense>
				</ErrorBoundary>
			</div>
		</section>
	);
}
